import React, { useState, useEffect, useRef } from 'react';
import { getSocket } from '../utils/socket';
import { getToken } from '../utils/auth';
import { API_URL } from '../config';
import './AdminGameControl.css';

function AdminGameControl({ session, onNavigateToDisplay }) {
  const [socket, setSocket] = useState(null);
  const [currentState, setCurrentState] = useState(session?.currentState || 'welcome');
  const [participantCount, setParticipantCount] = useState(
    session?.participants ? Object.keys(session.participants).length : 0
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState(session?.questions || []);
  const [completedQuestionIds, setCompletedQuestionIds] = useState(new Set());
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [answerCounts, setAnswerCounts] = useState({});
  const [error, setError] = useState('');
  const [showAddQuestions, setShowAddQuestions] = useState(false);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [addingQuestions, setAddingQuestions] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);

  // Ref so socket handlers always see the current questions array (avoids stale closure)
  const questionsRef = useRef(session?.questions || []);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  useEffect(() => {
    if (!session || !session.code) {
      setError('No session data available');
      return;
    }

    const socketInstance = getSocket();
    setSocket(socketInstance);

    loadQuestionGroups();

    socketInstance.emit('admin_join', { sessionCode: session.code });

    socketInstance.on('admin_joined', (data) => {
      setCurrentState(data.currentState);
      setParticipantCount(data.participantCount);
      setQuestions(data.questions || []);
      // Restore position and completion state after reconnect/reload
      if (typeof data.currentQuestionIndex === 'number' && data.currentQuestionIndex >= 0) {
        setCurrentQuestionIndex(data.currentQuestionIndex);
      }
      if (Array.isArray(data.completedQuestions)) {
        setCompletedQuestionIds(new Set(data.completedQuestions));
      }
      if (Array.isArray(data.participants)) {
        setParticipants(data.participants);
      }
    });

    socketInstance.on('participant_status', (data) => {
      setParticipants(prev => prev.map(p =>
        p.name === data.name ? { ...p, connected: data.connected } : p
      ));
    });

    socketInstance.on('participant_removed', (data) => {
      setParticipants(prev => prev.filter(p => p.name !== data.name));
      setParticipantCount(data.participantCount);
    });

    socketInstance.on('participant_joined', (data) => {
      setParticipantCount(data.participantCount);
      if (data.name) {
        setParticipants(prev => {
          if (prev.find(p => p.name === data.name)) {
            return prev.map(p => p.name === data.name ? { ...p, connected: true } : p);
          }
          return [...prev, { name: data.name, totalScore: 0, connected: true }];
        });
      }
    });

    socketInstance.on('question_started', (data) => {
      setActiveQuestionId(data.id);
    });

    socketInstance.on('new_response', (data) => {
      setAnswerCounts({ responseCount: data.responseCount, totalParticipants: data.totalParticipants });
    });

    socketInstance.on('question_ended', (data) => {
      const doneId = data.result.questionId;
      setActiveQuestionId(null);
      setAnswerCounts({});
      setCompletedQuestionIds(prev => {
        const next = new Set([...prev, doneId]);
        // Auto-advance to the first uncompleted question in the current list
        const nextIdx = questionsRef.current.findIndex(q => !next.has(q.id));
        if (nextIdx !== -1) setCurrentQuestionIndex(nextIdx);
        return next;
      });
    });

    socketInstance.on('state_changed', (data) => {
      setCurrentState(data.state);
    });

    socketInstance.on('error', (data) => {
      setError(data.message);
    });

    return () => {
      socketInstance.off('admin_joined');
      socketInstance.off('participant_joined');
      socketInstance.off('question_started');
      socketInstance.off('new_response');
      socketInstance.off('question_ended');
      socketInstance.off('state_changed');
      socketInstance.off('participant_status');
      socketInstance.off('participant_removed');
      socketInstance.off('error');
    };
  }, [session]);

  const handleShowWelcome = () => {
    socket.emit('admin_show_welcome', { sessionCode: session.code });
  };

  const handleStartQuestion = () => {
    if (currentQuestionIndex >= questions.length) {
      setError('No more questions available');
      return;
    }
    setError('');
    socket.emit('admin_start_question', {
      sessionCode: session.code,
      questionIndex: currentQuestionIndex
    });
  };

  const handleEndQuestion = () => {
    socket.emit('admin_end_question', { sessionCode: session.code });
  };

  const handleShowLeaderboard = () => {
    socket.emit('admin_show_leaderboard', { sessionCode: session.code });
  };

  const handleRemoveParticipant = (name) => {
    if (!window.confirm(`Remove "${name}" from the session?`)) return;
    socket.emit('admin_remove_participant', { sessionCode: session.code, name });
  };

  const handleSelectQuestion = (index) => {
    const q = questions[index];
    if (!q || completedQuestionIds.has(q.id) || activeQuestionId === q.id) return;
    setCurrentQuestionIndex(index);
    setError('');
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newQ = [...questions];
    [newQ[index - 1], newQ[index]] = [newQ[index], newQ[index - 1]];
    setQuestions(newQ);
    if (currentQuestionIndex === index) setCurrentQuestionIndex(index - 1);
    else if (currentQuestionIndex === index - 1) setCurrentQuestionIndex(index);
    socket.emit('reorder_questions', { sessionCode: session.code, questions: newQ });
  };

  const handleMoveDown = (index) => {
    if (index === questions.length - 1) return;
    const newQ = [...questions];
    [newQ[index], newQ[index + 1]] = [newQ[index + 1], newQ[index]];
    setQuestions(newQ);
    if (currentQuestionIndex === index) setCurrentQuestionIndex(index + 1);
    else if (currentQuestionIndex === index + 1) setCurrentQuestionIndex(index);
    socket.emit('reorder_questions', { sessionCode: session.code, questions: newQ });
  };

  const loadQuestionGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/game/question-groups`);
      const data = await response.json();
      setQuestionGroups(data.groups || []);
    } catch (err) {
      console.error('Failed to load question groups:', err);
    }
  };

  const handleAddQuestions = async () => {
    if (!selectedGroupId) {
      setError('Please select a question group');
      return;
    }
    setAddingQuestions(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('You are not logged in. Please login again.');
        return;
      }
      const response = await fetch(`${API_URL}/api/game/session/${session.code}/add-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ questionGroupId: selectedGroupId })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add questions');
      }
      const result = await response.json();
      const sessionResponse = await fetch(`${API_URL}/api/game/session/${session.code}`);
      const sessionData = await sessionResponse.json();
      setQuestions(sessionData.questions || []);
      setShowAddQuestions(false);
      setSelectedGroupId('');
      alert(`Added ${result.addedCount} questions. Total: ${result.totalQuestions}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingQuestions(false);
    }
  };

  if (!session) {
    return (
      <div className="agc-container">
        <div className="agc-error">No game session found. Please create a new game first.</div>
      </div>
    );
  }

  const completedCount = completedQuestionIds.size;
  const isQuestionActive = currentState === 'question' || currentState === 'countdown';
  const selectedQuestion = questions[currentQuestionIndex];
  const canStart = (
    !isQuestionActive &&
    selectedQuestion &&
    !completedQuestionIds.has(selectedQuestion?.id) &&
    currentQuestionIndex < questions.length
  );

  return (
    <div className="agc-container">

      {/* ── Header ── */}
      <div className="agc-header">
        <div className="agc-header-left">
          <span className="agc-session-code">{session.code}</span>
          <span className={`agc-state-badge state-${currentState}`}>
            {currentState.replace(/_/g, ' ').toUpperCase()}
          </span>
          <span className="agc-participant-count">👥 {participantCount}</span>
        </div>
        <div className="agc-header-right">
          <button
            className="agc-display-btn"
            onClick={() => window.open('/#admin-game-display', '_blank')}
          >
            📺 Open Display ↗
          </button>
        </div>
      </div>

      {error && (
        <div className="agc-error">
          {error}
          <button className="agc-error-dismiss" onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* ── Main body ── */}
      <div className="agc-body">

        {/* ── Left: Question Queue ── */}
        <div className="agc-queue-panel">
          <div className="queue-header">
            <h2>Question Queue</h2>
            <div className="queue-meta">
              <span className="queue-done-label">{completedCount}/{questions.length} done</span>
              <div className="queue-progress-bar">
                <div
                  className="queue-progress-fill"
                  style={{ width: questions.length > 0 ? `${(completedCount / questions.length) * 100}%` : '0%' }}
                />
              </div>
            </div>
          </div>

          <div className="question-list">
            {questions.map((question, index) => {
              const isCompleted = completedQuestionIds.has(question.id);
              const isThisActive = activeQuestionId === question.id;
              const isSelected = index === currentQuestionIndex && !isCompleted && !isThisActive;
              const canReorder = !isQuestionActive;

              let rowClass = 'q-row';
              if (isCompleted) rowClass += ' q-completed';
              else if (isThisActive) rowClass += ' q-active';
              else if (isSelected) rowClass += ' q-selected';
              else rowClass += ' q-pending';

              return (
                <div
                  key={question.id}
                  className={rowClass}
                  onClick={() => handleSelectQuestion(index)}
                  title={isCompleted ? 'Already played' : isThisActive ? 'Currently active' : 'Click to select'}
                >
                  <span className="q-status-icon">
                    {isCompleted ? '✅' : isThisActive ? '▶' : isSelected ? '●' : '○'}
                  </span>
                  <span className="q-number">{index + 1}</span>
                  <span className={`q-type-badge ${question.type === 'multiple_choice' ? 'badge-mcq' : 'badge-ft'}`}>
                    {question.type === 'multiple_choice' ? 'MCQ' : 'FT'}
                  </span>
                  <span className="q-text">{question.text}</span>
                  {isThisActive && answerCounts.totalParticipants > 0 && (
                    <span className="q-live-count">
                      ⚡ {answerCounts.responseCount}/{answerCounts.totalParticipants}
                    </span>
                  )}
                  <div className="q-reorder-btns">
                    <button
                      className="q-move-btn"
                      onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                      disabled={index === 0 || !canReorder}
                      title="Move up"
                    >↑</button>
                    <button
                      className="q-move-btn"
                      onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                      disabled={index === questions.length - 1 || !canReorder}
                      title="Move down"
                    >↓</button>
                  </div>
                </div>
              );
            })}

            {questions.length === 0 && (
              <div className="q-empty">No questions loaded. Add a question group below.</div>
            )}
          </div>

          {!isQuestionActive && questions.length > 0 && (
            <p className="queue-hint">Click any pending question to select it as next to run</p>
          )}
        </div>

        {/* ── Right: Controls ── */}
        <div className="agc-controls-panel">

          {/* Selected question preview */}
          {selectedQuestion && !completedQuestionIds.has(selectedQuestion.id) && activeQuestionId !== selectedQuestion.id && (
            <div className="selected-q-preview">
              <div className="selected-q-header">
                <span className="selected-q-label">Next up: Q{currentQuestionIndex + 1}</span>
                <span className={`q-type-badge ${selectedQuestion.type === 'multiple_choice' ? 'badge-mcq' : 'badge-ft'}`}>
                  {selectedQuestion.type === 'multiple_choice' ? 'MCQ' : 'FT'}
                </span>
                {selectedQuestion.timeLimit && (
                  <span className="selected-q-time">⏱ {selectedQuestion.timeLimit}s</span>
                )}
              </div>
              <p className="selected-q-text">{selectedQuestion.text}</p>
            </div>
          )}

          {/* Active question live stats */}
          {isQuestionActive && (
            <div className="active-q-panel">
              <div className="active-q-label">
                {currentState === 'countdown' ? '⏳ Countdown…' : '▶ Question in progress'}
              </div>
              {currentState === 'question' && answerCounts.totalParticipants > 0 && (
                <div className="active-q-stats">
                  <span className="active-q-count">{answerCounts.responseCount}</span>
                  <span className="active-q-total"> / {answerCounts.totalParticipants} answered</span>
                  <div className="active-answer-bar">
                    <div
                      className="active-answer-fill"
                      style={{
                        width: `${(answerCounts.responseCount / answerCounts.totalParticipants) * 100}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* All questions completed notice */}
          {!isQuestionActive && completedCount === questions.length && questions.length > 0 && (
            <div className="all-done-notice">
              All questions completed! Show the final leaderboard.
            </div>
          )}

          {/* Primary actions */}
          <div className="primary-actions">
            <button
              className="btn-start"
              onClick={handleStartQuestion}
              disabled={!canStart}
            >
              ▶ Start Q{currentQuestionIndex + 1}
            </button>
            <button
              className="btn-end"
              onClick={handleEndQuestion}
              disabled={currentState !== 'question'}
            >
              ⏹ End Question
            </button>
          </div>

          {/* Secondary actions */}
          <div className="secondary-actions">
            <button
              className="btn-welcome"
              onClick={handleShowWelcome}
              disabled={currentState === 'welcome'}
            >
              📋 Welcome Screen
            </button>
            <button
              className="btn-leaderboard"
              onClick={handleShowLeaderboard}
              disabled={currentState === 'finished' || completedCount === 0}
            >
              🏆 Final Leaderboard
            </button>
          </div>

          {/* QR code */}
          {session.qrCode && (
            <div className="agc-qr">
              <img src={session.qrCode} alt="Join QR" className="agc-qr-img" />
              <p className="agc-qr-url">{session.joinUrl}</p>
            </div>
          )}

          {/* Participants panel */}
          <div className="agc-participants-panel">
            <button
              className="agc-participants-toggle"
              onClick={() => setShowParticipants(v => !v)}
            >
              👥 Players ({participants.length}) {showParticipants ? '▲' : '▼'}
            </button>
            {showParticipants && (
              <div className="agc-participants-list">
                {participants.length === 0 && (
                  <div className="agc-participants-empty">No players yet</div>
                )}
                {participants.map(p => (
                  <div key={p.name} className="agc-participant-row">
                    <span className={`agc-conn-dot ${p.connected ? 'connected' : 'disconnected'}`} title={p.connected ? 'Online' : 'Offline'} />
                    <span className="agc-participant-name">{p.name}</span>
                    <span className="agc-participant-score">{p.totalScore} pts</span>
                    <button
                      className="agc-remove-btn"
                      onClick={() => handleRemoveParticipant(p.name)}
                      title="Remove player"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer: Add questions ── */}
      <div className="agc-footer">
        {!showAddQuestions ? (
          <button className="btn-add-questions" onClick={() => setShowAddQuestions(true)}>
            ➕ Add Questions from Group
          </button>
        ) : (
          <div className="add-q-form">
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              disabled={addingQuestions}
            >
              <option value="">— Select a question group —</option>
              {questionGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.questions.length} questions)
                </option>
              ))}
            </select>
            <button
              className="btn-confirm-add"
              onClick={handleAddQuestions}
              disabled={!selectedGroupId || addingQuestions}
            >
              {addingQuestions ? 'Adding…' : 'Add'}
            </button>
            <button
              className="btn-cancel-add"
              onClick={() => { setShowAddQuestions(false); setSelectedGroupId(''); }}
              disabled={addingQuestions}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

    </div>
  );
}

export default AdminGameControl;
